import { format } from 'date-fns'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { recordArticleRead } from '@/lib/usageEvents'

interface NewsArticle {
  id: string
  title: string
  description: string | null
  author: string
  publish_date: string | null
  image_url: string | null
  article_file_url: string | null
  is_displayed: boolean
  expiry_days: number | null
}

interface NewsCardProps {
  article: NewsArticle
}

export default function NewsCard({ article }: NewsCardProps) {
  const { user } = useAuth()
  const formattedDate = article.publish_date
    ? format(new Date(article.publish_date), 'dd/MM/yyyy')
    : null

  return (
    <div
      className="rounded-lg border-2 overflow-hidden flex"
      style={{
        backgroundColor: '#ffffff',
        borderColor: 'var(--bg-tertiary)',
        aspectRatio: '2 / 1',
      }}
    >
      {/* Article Image - Left Side */}
      {article.image_url && (
        <div className="w-1/3 h-full overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: '#f5f5f5' }}
        >
          <img
            src={article.image_url}
            alt={article.title}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
      
      {/* Content - Right Side */}
      <div className="flex-1 p-4 flex flex-col justify-between">
        <div className="min-w-0">
          <h3
            className="text-base font-bold mb-1 line-clamp-2"
            style={{ color: '#000000' }}
          >
            {article.title}
          </h3>
          
          {article.description && (
            <p
              className="mb-2 text-sm line-clamp-2"
              style={{ color: '#000000' }}
            >
              {article.description}
            </p>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5 min-w-0">
            <p
              className="text-xs font-semibold truncate"
              style={{ color: '#000000' }}
            >
              {article.author}
            </p>
            {formattedDate && (
              <p
                className="text-xs"
                style={{ color: '#000000' }}
              >
                {formattedDate}
              </p>
            )}
          </div>
          
          {article.article_file_url && (
            <a
              href={article.article_file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-semibold rounded flex-shrink-0"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
              }}
              onClick={async (e) => {
                e.preventDefault()
                await recordArticleRead(supabase, article.id, user?.id ?? null)
                window.open(article.article_file_url!, '_blank', 'noopener,noreferrer')
              }}
            >
              Read Article
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

